import { toast } from "sonner";
import { Layers, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PresetPicker, presetPatch } from "@/components/MenuTools";
import { inputCls } from "@/components/kit";
import { fmtMoney } from "@/lib/api";

const NAMES = ["Classico", "Elegante", "Esclusivo"];

function PackageSlot({ p, i, evento, spec, set, remove, apply, disabled }) {
  const courses = spec.menu.filter(([k]) => p[k]).map(([, l]) => l);
  return (
    <div className="rounded-xl border border-[#D4E6EC] bg-[#F5FAFB] p-4 space-y-3" data-testid={`package-${i}`}>
      <div className="flex gap-2">
        <input className={inputCls} disabled={disabled} value={p.nome || ""} onChange={(e) => set({ nome: e.target.value })} placeholder="Nome pacchetto" data-testid={`package-name-${i}`} />
        {!disabled && <button onClick={remove} className="text-[#991B1B]" data-testid={`package-remove-${i}`}><Trash2 size={15} /></button>}
      </div>
      {!disabled && <PresetPicker evento={evento} onApply={(pr) => { set(presetPatch(pr)); toast.success(`${pr.nome} nel pacchetto ${p.nome}`); }} />}
      <div className="grid grid-cols-2 gap-2">
        <input type="number" className={inputCls} disabled={disabled} value={p.prezzo_adulti || ""} onChange={(e) => set({ prezzo_adulti: e.target.value })} placeholder="€ adulti /pers." data-testid={`package-price-${i}`} />
        <input type="number" className={inputCls} disabled={disabled} value={p.prezzo_bambini || ""} onChange={(e) => set({ prezzo_bambini: e.target.value })} placeholder="€ bambini /pers." data-testid={`package-price-kids-${i}`} />
      </div>
      <div className="text-xs text-[#47606B]">{courses.length ? `Menù: ${courses.join(", ")}` : "Carica un menù preimpostato: senza menù vale quello del preventivo."}</div>
      {!disabled && <Button size="sm" variant="outline" className="rounded-full" onClick={apply} data-testid={`package-apply-${i}`}>Usa questo nel preventivo</Button>}
    </div>
  );
}

export function PackagesEditor({ q, spec, change, disabled }) {
  const list = q.data.pacchetti || [];
  const setList = (pacchetti) => change({ data: { ...q.data, pacchetti } });
  const apply = (p) => {
    const keep = Object.fromEntries(Object.entries(q.data).filter(([k]) => !k.startsWith("a_") || k === "a_formula"));
    const { nome, ...rest } = p;
    const has = spec.menu.some(([k]) => p[k]);
    change({ data: { ...(has ? keep : q.data), ...Object.fromEntries(Object.entries(rest).filter(([, v]) => v !== "" && v != null)), pacchetto_scelto: nome } });
    toast.success(`Pacchetto ${nome} applicato al preventivo`);
  };
  return (
    <section className="rounded-xl border border-[#D4E6EC] bg-white p-5 sm:p-7 space-y-4 fade-up" data-testid="quote-packages-card">
      <h3 className="font-serif-it text-2xl font-semibold flex items-center gap-2"><Layers size={20} /> Pacchetti a confronto</h3>
      <p className="text-sm text-[#47606B] -mt-2">Facoltativo: fino a 3 proposte. Il cliente le vede una accanto all'altra nel preventivo online e sceglie quella che preferisce.</p>
      <div className="grid md:grid-cols-3 gap-3">
        {list.map((p, i) => (
          <PackageSlot key={i} p={p} i={i} evento={q.evento_tipo} spec={spec} disabled={disabled} apply={() => apply(p)}
            set={(patch) => setList(list.map((x, j) => (j === i ? { ...x, ...patch } : x)))} remove={() => setList(list.filter((_, j) => j !== i))} />
        ))}
      </div>
      {!disabled && list.length < 3 && <Button size="sm" variant="outline" className="rounded-full" onClick={() => setList([...list, { nome: NAMES[list.length] }])} data-testid="package-add"><Plus size={13} className="mr-1" /> Aggiungi pacchetto</Button>}
      {q.data.pacchetto_scelto && <div className="text-xs text-[#166534]" data-testid="package-chosen">Pacchetto scelto: <b>{q.data.pacchetto_scelto}</b></div>}
    </section>
  );
}

export function PackageCards({ q, chosen, setChosen, locked }) {
  return (
    <div className="grid md:grid-cols-3 gap-4" data-testid="public-packages">
      {q.pacchetti.map((p, i) => {
        const on = chosen === i || (locked && q.pacchetto_scelto === p.nome);
        return (
          <button key={i} type="button" disabled={locked} onClick={() => setChosen(i)} data-testid={`public-package-${i}`}
            className={`text-left rounded-2xl border-2 p-5 space-y-3 bg-white transition-transform duration-200 hover:-translate-y-1 ${on ? "border-[#0B6E8F] shadow-lg" : "border-[#D4E6EC]"} ${i === 1 && q.pacchetti.length === 3 ? "md:-mt-3" : ""}`}>
            {i === 1 && q.pacchetti.length === 3 && <div className="text-[10px] uppercase tracking-[0.2em] text-[#34B3B0]">Il più scelto</div>}
            <div className="font-serif-it text-3xl font-semibold">{p.nome}</div>
            <div className="text-sm text-[#47606B]">{[p.formula, p.prezzo_adulti && `${fmtMoney(Number(p.prezzo_adulti))} a persona`].filter(Boolean).join(" • ")}</div>
            <div className="space-y-1.5 text-sm">{p.menu.map((m) => <div key={m.label}><div className="text-[10px] uppercase tracking-wider text-[#7B929C]">{m.label}</div><div className="whitespace-pre-line">{m.value}</div></div>)}</div>
            <div className="border-t border-[#D4E6EC] pt-3 flex justify-between items-end"><span className="text-xs text-[#7B929C]">Totale</span><span className="font-serif-it text-2xl font-semibold" data-testid={`public-package-total-${i}`}>{fmtMoney(p.totals.totale)}</span></div>
            <div className={`text-center text-xs rounded-full py-1.5 ${on ? "bg-[#0B6E8F] text-white" : "bg-[#EAF5F7] text-[#0B6E8F]"}`}>{on ? "Scelto" : "Scegli questo"}</div>
          </button>
        );
      })}
    </div>
  );
}
